import * as XLSX from 'xlsx';
import { todayFilenameStamp } from './export-coa';

// Tree node shape returned by account.getTree with includeMetadata: true.
// Kept locally instead of importing from the server router to avoid a
// frontend-on-server type dependency for a leaf utility.
export interface ExportableTravelAccountNode {
  id: string;
  code: string;
  name: string;
  type: string;
  subType: string | null;
  normalBalance: string;
  balance: number;
  currency: string;
  isActive: boolean;
  isSystemAccount: boolean;
  description?: string | null;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  children: ExportableTravelAccountNode[];
}

interface ExportRow {
  Code: string;
  Name: string;
  Type: string;
  'Sub-Type': string;
  'Normal Balance': string;
  'Parent Code': string;
  Balance: number;
  Currency: string;
  Active: string;
  'System Account': string;
  Description: string;
  'Created At': string;
  'Updated At': string;
}

function formatDate(value: Date | string | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// Depth-first flatten preserving code order. Travel accounts link to their
// parent by id (not by code), so the parent code is carried down from the
// tree nesting instead of being read off the node.
function flatten(
  nodes: ExportableTravelAccountNode[],
  depth: number,
  parentCode: string | null
): Array<{ node: ExportableTravelAccountNode; depth: number; parentCode: string | null }> {
  const out: Array<{
    node: ExportableTravelAccountNode;
    depth: number;
    parentCode: string | null;
  }> = [];
  for (const node of nodes) {
    out.push({ node, depth, parentCode });
    if (node.children.length > 0) {
      out.push(...flatten(node.children, depth + 1, node.code));
    }
  }
  return out;
}

function buildRow(
  node: ExportableTravelAccountNode,
  depth: number,
  parentCode: string | null,
  indented: boolean
): ExportRow {
  const namePrefix = indented ? '  '.repeat(depth) : '';
  return {
    Code: node.code,
    Name: `${namePrefix}${node.name}`,
    Type: node.type,
    'Sub-Type': node.subType ? node.subType.replace(/_/g, ' ') : '',
    'Normal Balance': node.normalBalance,
    'Parent Code': parentCode ?? '',
    Balance: Number(node.balance) || 0,
    Currency: node.currency,
    Active: node.isActive ? 'Yes' : 'No',
    'System Account': node.isSystemAccount ? 'Yes' : 'No',
    Description: node.description ?? '',
    'Created At': formatDate(node.createdAt),
    'Updated At': formatDate(node.updatedAt),
  };
}

export interface TravelExportOptions {
  indented: boolean;
  filename: string;
}

export function exportTravelChartOfAccounts(
  tree: ExportableTravelAccountNode[],
  opts: TravelExportOptions
): void {
  const flat = flatten(tree, 0, null);
  const rows: ExportRow[] = flat.map(({ node, depth, parentCode }) =>
    buildRow(node, depth, parentCode, opts.indented)
  );

  const worksheet = XLSX.utils.json_to_sheet(rows);

  // Column widths tuned to keep the file readable when opened in Excel
  worksheet['!cols'] = [
    { wch: 18 }, // Code
    { wch: 40 }, // Name
    { wch: 12 }, // Type
    { wch: 24 }, // Sub-Type
    { wch: 16 }, // Normal Balance
    { wch: 16 }, // Parent Code
    { wch: 16 }, // Balance
    { wch: 8 }, // Currency
    { wch: 8 }, // Active
    { wch: 8 }, // System Account
    { wch: 40 }, // Description
    { wch: 20 }, // Created At
    { wch: 20 }, // Updated At
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Chart of Accounts');
  XLSX.writeFile(workbook, opts.filename);
}

export { todayFilenameStamp };
